// The animation brain of the cartoons. Given an action, how far into it we are and how loud the character is speaking, it says where every joint is:
// the same answer drives the flat (2D) and the 3D drawing, so a character acts the same way in both.
//
// Angles are in radians. For an arm or a leg a POSITIVE angle swings it FORWARD (the way the character faces); 0 hangs straight down; ~3 is straight up.
// An elbow bends the forearm forward, a knee bends the shin backward.

export const ACTIONS = {
  idle: "Stand", talk: "Talk with gestures", walk: "Walk", run: "Run", wave: "Wave", jump: "Jump", dance: "Dance", laugh: "Laugh", cry: "Cry", sad: "Sad",
  point: "Point", cheer: "Cheer", sit: "Sit", angry: "Angry", scared: "Scared", think: "Think", nod: "Nod yes", shake: "Shake head no",
};
export const ACTION_IDS = Object.keys(ACTIONS);
export const MOVING = new Set(["walk", "run"]);

export const EMOTIONS = { neutral: "Neutral", happy: "Happy", sad: "Sad", angry: "Angry", scared: "Scared", surprised: "Surprised" };
const EMOTION_FACE = {
  neutral: { smile: 0.15, brow: 0, eyes: 1 }, happy: { smile: 0.9, brow: 0.25, eyes: 1 }, sad: { smile: -0.7, brow: -0.6, eyes: 0.8 },
  angry: { smile: -0.5, brow: -1, eyes: 0.85 }, scared: { smile: -0.3, brow: 0.8, eyes: 1.35 }, surprised: { smile: 0.1, brow: 1, eyes: 1.4 },
};
// the feeling an action carries when no other is given
const ACTION_EMOTION = { laugh: "happy", wave: "happy", jump: "happy", dance: "happy", cheer: "happy", cry: "sad", sad: "sad", angry: "angry", scared: "scared" };

const TAU = Math.PI * 2;
const sin = Math.sin;
const clamp = (x, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, x));
const smooth = (x) => { const t = clamp(x); return t * t * (3 - 2 * t); };

export const neutralPose = () => ({
  bob: 0, jump: 0, lean: 0, headTilt: 0, headNod: 0, headTurn: 0,
  armL: { s: 0.06, e: 0.12 }, armR: { s: 0.06, e: 0.12 }, legL: { h: 0, k: 0 }, legR: { h: 0, k: 0 },
  tail: 0, ear: 0, smile: 0.15, brow: 0, eyes: 1, mouth: 0, shake: 0,
});

// speech = { energy, nod } from lipsync.mouthAt (0 when the character is not speaking); `phase` = the walking cycle position in radians (distance based, so the feet do not slide).
export function poseAt(action, t, speech = { energy: 0, nod: 0 }, phase = 0, emotion = null, seed = 0) {
  const p = neutralPose();
  const e = speech.energy || 0;
  const s = seed * 1.3;
  // breathing and a little life in every action
  p.bob = sin(t * TAU * 0.33 + s) * 0.035;
  p.armL.s = 0.06 + sin(t * TAU * 0.3 + s) * 0.03;
  p.armR.s = 0.06 + sin(t * TAU * 0.3 + s + 1) * 0.03;
  p.headTilt = sin(t * TAU * 0.2 + s) * 0.04;
  p.tail = sin(t * TAU * 0.7 + s) * 0.25;
  p.ear = sin(t * TAU * 0.5 + s) * 0.05;

  switch (action) {
    case "talk": {
      // the hands move with the voice: the louder, the bigger
      const g1 = sin(t * TAU * 0.85 + s);
      const g2 = sin(t * TAU * 1.1 + s + 2);
      p.armR = { s: 0.5 + e * (0.7 + 0.5 * g1), e: 0.9 + e * 0.5 * g2 };
      p.armL = { s: 0.28 + e * (0.35 + 0.3 * g2), e: 0.7 + e * 0.4 * g1 };
      p.headNod = (speech.nod || 0) * 0.22 + e * 0.07 * sin(t * TAU * 2.2);
      p.headTilt = sin(t * TAU * 0.5 + s) * 0.07;
      p.lean = e * 0.04 * g1;
      break;
    }
    case "walk": case "run": {
      const fast = action === "run";
      const a = fast ? 0.95 : 0.55;
      p.legL = { h: sin(phase) * a, k: Math.max(0, sin(phase + Math.PI / 2)) * (fast ? 1.3 : 0.75) };
      p.legR = { h: -sin(phase) * a, k: Math.max(0, -sin(phase + Math.PI / 2)) * (fast ? 1.3 : 0.75) };
      p.armL = { s: -sin(phase) * (fast ? 0.95 : 0.5), e: fast ? 1.3 : 0.35 };
      p.armR = { s: sin(phase) * (fast ? 0.95 : 0.5), e: fast ? 1.3 : 0.35 };
      p.bob = Math.abs(sin(phase)) * (fast ? 0.2 : 0.1);
      p.lean = fast ? 0.2 : 0.06;
      p.headTilt = sin(phase * 2) * 0.03;
      p.tail = sin(phase) * 0.5;
      break;
    }
    case "wave":
      p.armR = { s: 2.7, e: 0.5 + sin(t * TAU * 2.4) * 0.55 };
      p.armL = { s: 0.12, e: 0.2 };
      p.headTilt = 0.08;
      break;
    case "jump": {
      const u = (t % 1.0);
      p.jump = Math.max(0, sin(Math.PI * u)) * 1.3;
      const air = p.jump > 0.05;
      p.armL = { s: air ? 2.5 : 0.4, e: 0.3 };
      p.armR = { s: air ? 2.5 : 0.4, e: 0.3 };
      p.legL = { h: air ? 0.5 : -0.1, k: air ? 0.9 : 0.5 };
      p.legR = { h: air ? 0.3 : -0.1, k: air ? 1.1 : 0.5 };
      p.bob = air ? 0 : -0.25 * (1 - sin(Math.PI * u + 0.01));
      break;
    }
    case "cheer": {
      const u = sin(t * TAU * 1.5);
      p.armL = { s: 2.8 + u * 0.15, e: 0.25 };
      p.armR = { s: 2.8 - u * 0.15, e: 0.25 };
      p.jump = Math.max(0, sin(t * TAU * 1.5)) * 0.35;
      break;
    }
    case "dance": {
      const b = sin(t * TAU * 1.7);
      p.bob = Math.abs(b) * 0.16;
      p.lean = b * 0.1;
      p.armL = { s: 1.9 + sin(t * TAU * 1.7) * 0.8, e: 0.6 };
      p.armR = { s: 1.9 - sin(t * TAU * 1.7) * 0.8, e: 0.6 };
      p.legL = { h: sin(t * TAU * 1.7) * 0.35, k: Math.max(0, sin(t * TAU * 1.7)) * 0.5 };
      p.legR = { h: -sin(t * TAU * 1.7) * 0.35, k: Math.max(0, -sin(t * TAU * 1.7)) * 0.5 };
      p.headTilt = b * 0.12;
      p.tail = b * 0.7;
      break;
    }
    case "laugh":
      p.shake = sin(t * TAU * 7) * 0.04;
      p.bob = Math.abs(sin(t * TAU * 3.5)) * 0.08;
      p.lean = -0.1;
      p.headNod = -0.25;
      p.armL = { s: 0.6, e: 1.5 };
      p.armR = { s: 0.6, e: 1.5 };
      p.mouth = 0.55 + 0.35 * Math.abs(sin(t * TAU * 3.5));
      break;
    case "cry":
      p.lean = 0.13;
      p.headNod = 0.35;
      p.shake = sin(t * TAU * 5) * 0.015;
      p.armL = { s: 2.2, e: 2.4 };
      p.armR = { s: 2.2, e: 2.4 };
      p.mouth = 0.25 + 0.15 * Math.abs(sin(t * TAU * 2.2));
      break;
    case "sad":
      p.lean = 0.14;
      p.headNod = 0.3;
      p.armL = { s: 0.0, e: 0.05 };
      p.armR = { s: 0.0, e: 0.05 };
      p.bob = -0.04;
      break;
    case "point":
      p.armR = { s: 1.45 + sin(t * TAU * 1.2) * 0.05, e: 0.05 };
      p.headTilt = -0.05;
      p.lean = 0.05;
      break;
    case "sit":
      p.bob = -1.15;
      p.legL = { h: 1.45, k: 0.15 };
      p.legR = { h: 1.45, k: 0.15 };
      p.armL = { s: 0.35, e: 0.7 };
      p.armR = { s: 0.35, e: 0.7 };
      break;
    case "angry":
      p.lean = 0.12;
      p.shake = sin(t * TAU * 9) * 0.012;
      p.armL = { s: 0.7, e: 1.8 };
      p.armR = { s: 0.75, e: 1.8 };
      p.headNod = 0.12;
      break;
    case "scared":
      p.shake = sin(t * TAU * 14) * 0.03;
      p.lean = -0.1;
      p.armL = { s: 1.7, e: 1.9 };
      p.armR = { s: 1.7, e: 1.9 };
      p.legL = { h: 0.05, k: 0.25 };
      p.legR = { h: 0.0, k: 0.3 };
      p.mouth = 0.4;
      break;
    case "think":
      p.armR = { s: 1.95, e: 2.5 };
      p.armL = { s: 0.5, e: 1.4 };
      p.headTilt = 0.14;
      p.headNod = 0.08;
      break;
    case "nod":
      p.headNod = 0.22 + sin(t * TAU * 1.8) * 0.2;
      break;
    case "shake":
      p.headTurn = sin(t * TAU * 1.8) * 0.5;
      p.headTilt = sin(t * TAU * 1.8) * 0.06;
      break;
    default: break;
  }

  const face = EMOTION_FACE[emotion || ACTION_EMOTION[action] || "neutral"] || EMOTION_FACE.neutral;
  p.smile = face.smile;
  p.brow = face.brow;
  p.eyes = face.eyes;
  return p;
}

// How a character blends from one pose to the next so that a change of action is not a jump. `amount` 0..1.
export function blendPoses(a, b, amount) {
  const k = smooth(amount);
  const mix = (x, y) => x + (y - x) * k;
  const arm = (u, v) => ({ s: mix(u.s, v.s), e: mix(u.e, v.e) });
  const leg = (u, v) => ({ h: mix(u.h, v.h), k: mix(u.k, v.k) });
  const out = {};
  Object.keys(a).forEach((key) => {
    if (key === "armL" || key === "armR") out[key] = arm(a[key], b[key]);
    else if (key === "legL" || key === "legR") out[key] = leg(a[key], b[key]);
    else out[key] = mix(a[key], b[key]);
  });
  return out;
}
